import React, { useState } from 'react';
import { X, Search } from 'lucide-react';
import { WidgetDefinition, WidgetType, WidgetSize } from '../../types/Dashboard';
import {
  Users, DollarSign, GraduationCap, AlertTriangle, TrendingUp, BarChart3,
  LineChart, PieChart, AreaChart, List, Table, Activity, Bell, GitCompare,
  FileText, Award, CheckCircle
} from 'lucide-react';

interface WidgetLibraryProps {
  isOpen: boolean;
  onClose: () => void;
  onAddWidget: (widget: WidgetDefinition) => void;
  existingWidgets?: Array<{ type: string; config?: Record<string, any> }>;
}

const widgetDefinitions: WidgetDefinition[] = [
  // Métricas
  { id: 'alunos-ativos', type: 'metric-card', name: 'Alunos Ativos', description: 'Total de alunos matriculados', category: 'metrics', defaultSize: 'small', icon: 'Users', availableSizes: ['small', 'medium'] },
  { id: 'mensalidades-pagas', type: 'metric-card', name: 'Mensalidades Pagas', description: 'Valor recebido no mês', category: 'metrics', defaultSize: 'small', icon: 'DollarSign', availableSizes: ['small', 'medium'] },
  { id: 'turmas-ativas', type: 'metric-card', name: 'Turmas Ativas', description: 'Quantidade de turmas ativas', category: 'metrics', defaultSize: 'small', icon: 'GraduationCap', availableSizes: ['small', 'medium'] },
  { id: 'inadimplencia', type: 'metric-card', name: 'Inadimplência', description: 'Valor e quantidade de mensalidades vencidas', category: 'metrics', defaultSize: 'small', icon: 'AlertTriangle', availableSizes: ['small', 'medium'] },
  { id: 'receita-total', type: 'metric-card', name: 'Receita Total', description: 'Receita acumulada do mês', category: 'metrics', defaultSize: 'small', icon: 'TrendingUp', availableSizes: ['small', 'medium'] },
  { id: 'taxa-ocupacao', type: 'metric-card', name: 'Taxa de Ocupação', description: 'Média de ocupação das turmas', category: 'metrics', defaultSize: 'small', icon: 'BarChart3', availableSizes: ['small', 'medium'] },
  
  // Gráficos Temporais
  { id: 'receita-vs-alunos', type: 'line-chart', name: 'Receita vs Alunos', description: 'Evolução comparativa últimos 6 meses', category: 'charts', defaultSize: 'large', icon: 'LineChart', availableSizes: ['medium', 'large'] },
  { id: 'receita-mensal', type: 'bar-chart', name: 'Receita Mensal', description: 'Receita por mês últimos 12 meses', category: 'charts', defaultSize: 'large', icon: 'BarChart3', availableSizes: ['medium', 'large'] },
  { id: 'evolucao-alunos', type: 'area-chart', name: 'Evolução de Alunos', description: 'Crescimento de alunos ao longo do tempo', category: 'charts', defaultSize: 'large', icon: 'AreaChart', availableSizes: ['medium', 'large'] },
  { id: 'mensalidades-pagas-vs-pendentes', type: 'bar-chart', name: 'Mensalidades Pagas vs Pendentes', description: 'Comparativo mensal', category: 'charts', defaultSize: 'medium', icon: 'BarChart3', availableSizes: ['medium', 'large'] },
  
  // Gráficos de Distribuição
  { id: 'distribuicao-niveis', type: 'pie-chart', name: 'Distribuição por Níveis', description: 'Alunos por nível', category: 'charts', defaultSize: 'medium', icon: 'PieChart', availableSizes: ['small', 'medium', 'large'] },
  { id: 'distribuicao-idades', type: 'pie-chart', name: 'Distribuição de Idades', description: 'Faixas etárias', category: 'charts', defaultSize: 'medium', icon: 'PieChart', availableSizes: ['small', 'medium', 'large'] },
  
  // Listas
  { id: 'agenda-hoje', type: 'list', name: 'Agenda de Hoje', description: 'Turmas do dia atual', category: 'lists', defaultSize: 'medium', icon: 'List', availableSizes: ['small', 'medium', 'large'] },
  { id: 'proximos-vencimentos', type: 'list', name: 'Próximos Vencimentos', description: 'Mensalidades que vencem nos próximos 7 dias', category: 'lists', defaultSize: 'medium', icon: 'List', availableSizes: ['small', 'medium', 'large'] },
  { id: 'alunos-recentes', type: 'list', name: 'Alunos Recentes', description: 'Últimos alunos cadastrados', category: 'lists', defaultSize: 'medium', icon: 'List', availableSizes: ['small', 'medium', 'large'] },
  { id: 'top-5-turmas', type: 'list', name: 'Top 5 Turmas', description: 'Turmas com maior ocupação', category: 'lists', defaultSize: 'medium', icon: 'List', availableSizes: ['small', 'medium', 'large'] },
  { id: 'mensalidades-vencidas', type: 'list', name: 'Mensalidades Vencidas', description: 'Lista detalhada de inadimplentes', category: 'lists', defaultSize: 'medium', icon: 'List', availableSizes: ['small', 'medium', 'large'] },
  
  // Estatísticas
  { id: 'taxa-inadimplencia', type: 'stat-card', name: 'Taxa de Inadimplência', description: 'Percentual e valor', category: 'statistics', defaultSize: 'small', icon: 'Activity', availableSizes: ['small', 'medium'] },
  { id: 'media-mensalidade', type: 'stat-card', name: 'Média de Mensalidade', description: 'Valor médio das mensalidades', category: 'statistics', defaultSize: 'small', icon: 'Activity', availableSizes: ['small', 'medium'] },
  { id: 'crescimento-mensal', type: 'stat-card', name: 'Crescimento Mensal', description: 'Percentual de crescimento', category: 'statistics', defaultSize: 'small', icon: 'TrendingUp', availableSizes: ['small', 'medium'] },
  { id: 'mes-atual-vs-anterior', type: 'comparison', name: 'Este Mês vs Mês Anterior', description: 'Comparativo de receita', category: 'statistics', defaultSize: 'medium', icon: 'GitCompare', availableSizes: ['small', 'medium', 'large'] },
  
  // Alertas
  { id: 'alertas-sistema', type: 'alert-list', name: 'Alertas do Sistema', description: 'Mensalidades vencidas, turmas sem professor, etc.', category: 'alerts', defaultSize: 'medium', icon: 'Bell', availableSizes: ['small', 'medium', 'large'] },
  
  // Avaliações
  { id: 'taxa-aprovacao', type: 'evaluation-report', name: 'Taxa de Aprovação', description: 'Percentual de alunos aprovados nas avaliações', category: 'evaluations', defaultSize: 'small', icon: 'CheckCircle', availableSizes: ['small', 'medium'] },
  { id: 'avaliacoes-recentes', type: 'evaluation-report', name: 'Avaliações Recentes', description: 'Lista das últimas avaliações realizadas', category: 'evaluations', defaultSize: 'medium', icon: 'FileText', availableSizes: ['small', 'medium', 'large'] },
  { id: 'evolucao-aprovacoes', type: 'evaluation-report', name: 'Evolução de Aprovações', description: 'Taxa de aprovação ao longo do tempo', category: 'evaluations', defaultSize: 'large', icon: 'LineChart', availableSizes: ['medium', 'large'] },
  { id: 'avaliacoes-por-nivel', type: 'evaluation-report', name: 'Avaliações por Nível', description: 'Quantidade de avaliações por nível', category: 'evaluations', defaultSize: 'medium', icon: 'BarChart3', availableSizes: ['medium', 'large'] },
  { id: 'avaliacoes-por-professor', type: 'evaluation-report', name: 'Avaliações por Professor', description: 'Distribuição de avaliações por professor', category: 'evaluations', defaultSize: 'medium', icon: 'BarChart3', availableSizes: ['medium', 'large'] },
  { id: 'media-notas-por-nivel', type: 'evaluation-report', name: 'Média de Notas por Nível', description: 'Comparativo de desempenho entre níveis', category: 'evaluations', defaultSize: 'medium', icon: 'Award', availableSizes: ['medium', 'large'] },
  { id: 'top-10-alunos', type: 'evaluation-report', name: 'Top 10 Alunos', description: 'Ranking de alunos por nota geral', category: 'evaluations', defaultSize: 'medium', icon: 'Award', availableSizes: ['medium', 'large'] },
];

const categoryLabels: { [key: string]: string } = {
  metrics: 'Métricas',
  charts: 'Gráficos',
  lists: 'Listas',
  statistics: 'Estatísticas',
  alerts: 'Alertas',
  evaluations: 'Avaliações'
};

const iconMap: { [key: string]: React.ReactNode } = {
  Users: <Users className="h-5 w-5" />,
  DollarSign: <DollarSign className="h-5 w-5" />,
  GraduationCap: <GraduationCap className="h-5 w-5" />,
  AlertTriangle: <AlertTriangle className="h-5 w-5" />,
  TrendingUp: <TrendingUp className="h-5 w-5" />,
  BarChart3: <BarChart3 className="h-5 w-5" />,
  LineChart: <LineChart className="h-5 w-5" />,
  PieChart: <PieChart className="h-5 w-5" />,
  AreaChart: <AreaChart className="h-5 w-5" />,
  List: <List className="h-5 w-5" />,
  Table: <Table className="h-5 w-5" />,
  Activity: <Activity className="h-5 w-5" />,
  Bell: <Bell className="h-5 w-5" />,
  GitCompare: <GitCompare className="h-5 w-5" />,
  FileText: <FileText className="h-5 w-5" />,
  Award: <Award className="h-5 w-5" />,
  CheckCircle: <CheckCircle className="h-5 w-5" />
};

const WidgetLibrary: React.FC<WidgetLibraryProps> = ({ isOpen, onClose, onAddWidget, existingWidgets = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  if (!isOpen) return null;

  // Verifica se um widget já está adicionado
  const isWidgetAdded = (widget: WidgetDefinition): boolean => {
    return existingWidgets.some(existing => {
      if (existing.type !== widget.type) return false;
      
      // Verifica o config baseado no tipo de widget
      const configKey = widget.type === 'metric-card' ? 'metricType' :
                       ['line-chart', 'bar-chart', 'pie-chart', 'area-chart'].includes(widget.type) ? 'chartType' :
                       widget.type === 'list' ? 'listType' :
                       widget.type === 'stat-card' ? 'statType' :
                       widget.type === 'comparison' ? 'comparisonType' :
                       widget.type === 'evaluation-report' ? 'reportType' : 'type';
      
      return existing.config?.[configKey] === widget.id;
    });
  };

  const filteredWidgets = widgetDefinitions.filter(widget => {
    const matchesSearch = widget.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         widget.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || widget.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = Array.from(new Set(widgetDefinitions.map(w => w.category)));

  const handleAddWidget = (widget: WidgetDefinition) => {
    if (!isWidgetAdded(widget)) {
      onAddWidget(widget);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 dark:text-gray-100">Biblioteca de Widgets</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500 dark:text-gray-400 dark:text-gray-500 dark:text-gray-400 dark:text-gray-500" />
          </button>
        </div>

        <div className="p-6 flex-1 overflow-y-auto">
          <div className="mb-6">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Buscar widgets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedCategory === null
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                }`}
              >
                Todos
              </button>
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                  }`}
                >
                  {categoryLabels[category]}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredWidgets.map(widget => {
              const isAdded = isWidgetAdded(widget);
              return (
                <div
                  key={widget.id}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    isAdded
                      ? 'border-primary-500 bg-primary-50 cursor-not-allowed'
                      : 'border-gray-200 dark:border-gray-700 hover:border-primary-500 hover:shadow-md cursor-pointer'
                  }`}
                  onClick={() => !isAdded && handleAddWidget(widget)}
                >
                  <div className="flex items-start gap-3 mb-2">
                    <div className={`p-2 rounded-lg ${
                      isAdded ? 'bg-primary-600 text-white' : 'bg-primary-100 text-primary-600'
                    }`}>
                      {iconMap[widget.icon] || <BarChart3 className="h-5 w-5" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-semibold text-sm ${
                          isAdded ? 'text-primary-900' : 'text-gray-900 dark:text-gray-100'
                        }`}>
                          {widget.name}
                        </h3>
                        {isAdded && (
                          <span className="text-xs px-2 py-0.5 bg-primary-600 text-white rounded-full font-medium">
                            Adicionado
                          </span>
                        )}
                      </div>
                      <p className={`text-xs mt-1 ${
                        isAdded ? 'text-primary-700' : 'text-gray-500 dark:text-gray-400 dark:text-gray-500'
                      }`}>
                        {widget.description}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className={`text-xs ${
                      isAdded ? 'text-primary-700' : 'text-gray-500 dark:text-gray-400 dark:text-gray-500'
                    }`}>
                      {categoryLabels[widget.category]}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      isAdded
                        ? 'bg-primary-200 text-primary-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {widget.defaultSize}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WidgetLibrary;

